import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { attemptFullscreenToggle } from "../../../../utilities";
import { Sprite, Container } from "@pixi/react";
import { useButtonHover } from "../../../../Hooks/useButtonHover";
import { SOUND_NAME } from "../../../../sounds";
import { getUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import {
  openBetSelectPopup,
  closeBetSelectPopup,
} from "../../../../State/actions";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import {
  selectBetSelectPopupActive,
  selectCriticalAnimationPlaying,
  selectHoldAndWinActive,
  selectIsSpinning,
  selectAutoPlayActive,
  selectFreeRoundsActive,
  selectUserInterfaceDisabled,
  selectFreeSpinActive,
} from "../../../../State/selectors";

// Same config as ButtonTurbo - identical size on all devices
const BET_SELECT_BUTTON_CONFIG = {
  BASE_WIDTH: 80,
  BASE_HEIGHT: 80,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.965,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.75,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.3,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.7,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

// Position config — extracted for easy tweaking
const BET_SELECT_POSITION_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => toolbar.width / 2 + buttonConfig.width * 1.7,
      y: (toolbar, buttonConfig, dimensions) => -dimensions.height / 11,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.185,
      y: (toolbar, buttonConfig, dimensions) => -dimensions.height / 2 - buttonConfig.height * 0.75,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => toolbar.width  - buttonConfig.width * 0.95,
      y: (toolbar, buttonConfig, dimensions) => toolbar.height - buttonConfig.height * 2,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.2,
      y: (toolbar, buttonConfig) => toolbar.height / 2 - buttonConfig.height * 6.7,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 0.9,
    y: (toolbar, buttonConfig) => toolbar.height / 2,
  },
};

const ButtonBetSelect = ({ toolbar }) => {
  const dispatch = useDispatch();
  const popupActive = useSelector(selectBetSelectPopupActive);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const isSpinning = useSelector(selectIsSpinning);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const freeRoundsActive = useSelector(selectFreeRoundsActive);
  const userInterfaceDisabled = useSelector(selectUserInterfaceDisabled);
  const freeSpinActive = useSelector(selectFreeSpinActive);
  const { getResponsiveConfig, device, orientation, dimensions } =
    useDimensions();

  // Scale hover + press effect
  const { hoverContainerRef, buttonRef, handlePointerOver, handlePointerOut, triggerPress } = useButtonHover();

  const texture = getUITexture("icon_bet");

  // Get responsive configuration from context - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(BET_SELECT_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  // Same positioning as ButtonTurbo (taking its place)
  const buttonPosition = useMemo(() => {
    const deviceConfig = BET_SELECT_POSITION_CONFIG[device];
    const posConfig = deviceConfig?.[orientation] ?? deviceConfig;
    return {
      x: posConfig.x(toolbar, buttonConfig, dimensions),
      y: posConfig.y(toolbar, buttonConfig, dimensions),
    };
  }, [
    device,
    orientation,
    toolbar,
    buttonConfig,
    dimensions,
  ]);

  // Calculate if button should be disabled - prevent during spins, critical animations, autoplay, or free rounds
  const isDisabled =
    userInterfaceDisabled ||
    criticalAnimationPlaying ||
    holdAndWinActive ||
    isSpinning ||
    autoPlayActive ||
    freeSpinActive ||
    (FEATURES_CONFIG.FREE_ROUNDS.ENABLED && freeRoundsActive);

  const handleClick = () => {
    // Attempt to toggle fullscreen if conditions are met
    attemptFullscreenToggle(dispatch, device);

    // Disable button during critical animations or hold and win feature
    if (isDisabled) {
      return;
    }

    soundManager.play(SOUND_NAME.button_click);

    triggerPress();

    // Toggle bet select popup - close if open, open if closed
    if (popupActive) {
      dispatch(closeBetSelectPopup());
    } else {
      dispatch(openBetSelectPopup());
    }
  };






  return (
    <Container name="secondary_button_bet_select" x={buttonPosition.x} y={buttonPosition.y}>
      {/* Hover container — scale animates here */}
      <Container ref={hoverContainerRef}>
        <Sprite
          ref={buttonRef}
          texture={texture}
          name="button_bet_select_icon"
          anchor={0.5}
          width={buttonConfig.width}
          height={buttonConfig.height}
          x={0}
          y={0}
          eventMode={isDisabled ? "none" : "static"}
          cursor={isDisabled ? "default" : "pointer"}
          alpha={isDisabled ? 0.7 : 1}
          onpointertap={handleClick}
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
        />
      </Container>
    </Container>
  );
};

export default ButtonBetSelect;
